import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Modal } from '../../components/Modal';
import { ConfirmModal } from '../../components/ConfirmModal';
import { useToast } from '../../components/Toast';
import { UpperInput } from '../../components/inputs/UpperInput';
import { useStickyHeader } from '../../lib/useStickyHeader';
import { MenuAcoesLinha, type AcaoLinha } from '../../components/MenuAcoesLinha';
import { StatusBadge } from '../../components/StatusBadge';
import { Pencil, Trash2, Ban, CheckCircle2 } from 'lucide-react';

interface Unidade {
  id: number;
  sigla: string;
  nome: string;
  permite_fracionado: boolean;
  // Migration 0311: inativa some das listas de escolha; quem ja usa a unidade continua com ela.
  ativo: boolean;
}

export function UnidadesMedida() {
  const toast = useToast();
  const [itens, setItens] = useState<Unidade[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');
  const [editando, setEditando] = useState<Unidade | null>(null);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState<{ sigla: string; nome: string; permite_fracionado: boolean }>({ sigla: '', nome: '', permite_fracionado: false });
  const [confirmar, setConfirmar] = useState<Unidade | null>(null);
  const [confirmarInativar, setConfirmarInativar] = useState<Unidade | null>(null);
  const [mostrarInativas, setMostrarInativas] = useState(false);

  async function carregar() {
    setCarregando(true);
    try {
      const lista = await api.get<Unidade[]>('/api/unidades-medida');
      setItens(lista);
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setCarregando(false);
    }
  }
  useEffect(() => { void carregar(); }, []);

  const filtrados = itens.filter((u) =>
    (mostrarInativas || u.ativo)
    && (u.sigla.toLowerCase().includes(busca.toLowerCase()) || u.nome.toLowerCase().includes(busca.toLowerCase())),
  );

  async function definirAtivo(u: Unidade, ativo: boolean) {
    try {
      await api.put(`/api/unidades-medida/${u.id}`, { ativo });
      toast.sucesso(ativo ? 'reativada' : 'inativada — os produtos que ja usam esta unidade continuam com ela');
      setConfirmarInativar(null);
      void carregar();
    } catch (e: any) {
      toast.erro(e.message);
    }
  }

  function abrirNovo() {
    setEditando(null);
    setForm({ sigla: '', nome: '', permite_fracionado: false });
    setModal(true);
  }
  function abrirEditar(u: Unidade) {
    setEditando(u);
    setForm({ sigla: u.sigla, nome: u.nome, permite_fracionado: u.permite_fracionado });
    setModal(true);
  }

  async function salvar() {
    try {
      if (editando) {
        await api.put(`/api/unidades-medida/${editando.id}`, form);
        toast.sucesso('atualizada');
      } else {
        await api.post('/api/unidades-medida', form);
        toast.sucesso('criada');
      }
      setModal(false);
      void carregar();
    } catch (e: any) {
      toast.erro(e.message);
    }
  }

  async function remover() {
    if (!confirmar) return;
    try {
      await api.delete(`/api/unidades-medida/${confirmar.id}`);
      toast.sucesso('removida');
      setConfirmar(null);
      void carregar();
    } catch (e: any) {
      toast.erro(e.message);
    }
  }

  const sticky = useStickyHeader();

  return (
    <div>
      <div ref={sticky.headerRef} style={sticky.headerStyle}>
        <div className="pagina-header">
          <h2 className="pagina-titulo">Unidades de medida</h2>
          <div className="pagina-acoes">
            <input type="search" placeholder="Buscar..." className="busca" value={busca} onChange={(e) => setBusca(e.target.value)} />
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
              <input type="checkbox" checked={mostrarInativas} onChange={(e) => setMostrarInativas(e.target.checked)} />
              Mostrar inativas
            </label>
            <button type="button" className="btn-primario" onClick={abrirNovo}>+ Nova</button>
          </div>
        </div>
      </div>

      {carregando ? <p className="muted">Carregando...</p> : filtrados.length === 0 ? <p className="muted">Nenhum registro.</p> : (
        <table className="tabela" style={sticky.tableStyle}>
          <thead><tr>
            <th style={sticky.thStyle}>Sigla</th>
            <th style={sticky.thStyle}>Nome</th>
            <th style={sticky.thStyle}>Fracionado?</th>
            <th style={{ ...sticky.thStyle, width: 100 }}>Status</th>
            <th style={{ ...sticky.thStyle, textAlign: 'right' }}>Acoes</th>
          </tr></thead>
          <tbody>
            {filtrados.map((u) => {
              const acoes: AcaoLinha[] = [
                { label: 'Editar', icon: Pencil, onClick: () => abrirEditar(u) },
                { label: 'Inativar', icon: Ban, perigoso: true, oculto: !u.ativo, onClick: () => setConfirmarInativar(u) },
                { label: 'Reativar', icon: CheckCircle2, oculto: u.ativo, onClick: () => void definirAtivo(u, true) },
                { label: 'Remover', icon: Trash2, perigoso: true, onClick: () => setConfirmar(u) },
              ];
              return (
                <tr key={u.id} onDoubleClick={() => abrirEditar(u)}>
                  <td>{u.sigla}</td>
                  <td>{u.nome}</td>
                  <td>{u.permite_fracionado ? 'Sim' : 'Nao'}</td>
                  <td><StatusBadge ativo={u.ativo} labelAtivo="Ativa" labelInativo="Inativa" /></td>
                  <td style={{ textAlign: 'right' }}>
                    <MenuAcoesLinha acoes={acoes} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <Modal aberto={modal} onFechar={() => setModal(false)} titulo={editando ? 'Editar unidade' : 'Nova unidade'} largura={460}
        rodape={
          <>
            <button type="button" className="btn-secundario" onClick={() => setModal(false)}>Cancelar</button>
            <button type="button" className="btn-primario" onClick={salvar} disabled={!form.sigla || !form.nome}>Salvar</button>
          </>
        }
      >
        <div className="form-grid">
          <label>
            Sigla
            <UpperInput value={form.sigla} onChange={(v) => setForm({ ...form, sigla: v })} maxLength={10} autoFocus />
          </label>
          <label>
            Nome
            <UpperInput value={form.nome} onChange={(v) => setForm({ ...form, nome: v })} maxLength={50} />
          </label>
          <label className="full" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={form.permite_fracionado}
              onChange={(e) => setForm({ ...form, permite_fracionado: e.target.checked })}
            />
            Permite quantidade fracionada
          </label>
        </div>
      </Modal>

      <ConfirmModal
        aberto={!!confirmarInativar}
        titulo="Inativar"
        mensagem={`Inativar a unidade "${confirmarInativar?.sigla}"? Ela deixa de aparecer nas listas de escolha; os produtos que ja usam esta unidade continuam com ela.`}
        textoConfirmar="Inativar"
        perigoso
        onConfirmar={() => { if (confirmarInativar) void definirAtivo(confirmarInativar, false); }}
        onCancelar={() => setConfirmarInativar(null)}
      />
      <ConfirmModal aberto={!!confirmar} titulo="Remover" mensagem={`Remover unidade "${confirmar?.sigla}"?`} textoConfirmar="Remover" perigoso onConfirmar={remover} onCancelar={() => setConfirmar(null)} />
    </div>
  );
}
